import { Stack } from '@mui/material'
import BarchartInfoLogo from 'assets/BarchartInfoLogo'
import { FormEvent, useState } from 'react'
import { COLORS } from 'styles/colors'
import axAPI from '../../httpConfig'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { notifyUser } from '../../store/notificationsSlice'
import {
	changeComponentInfo,
	fetchRationComponents,
} from '../../store/rationSystemSlice'
import { ButtonUI } from '../../ui/buttons/ButtonUI'
import Drawer from '../../ui/Drawer'
import { TypographyPrimary } from '../../ui/typography/TypographyPrimary'
import { TypographySecondary14 } from '../../ui/typography/TypographySecondary14'
import {
	FloatingField,
	getDuplicateIndexes,
	isCompleteDryMatter,
	sanitizeDryMatterInput,
} from './RationComponentField'

type PopupRationEditCompProps = {
	openEditComp: boolean
	setOpenEditComp: (open: boolean) => void
	title: string
	dry_matter_percent: number | string
	id: number
}

const toDryMatterInput = (value: number | string) =>
	String(value ?? '').replace(',', '.')

const PopupRationEditComp = ({
	openEditComp,
	setOpenEditComp,
	title,
	dry_matter_percent,
	id,
}: PopupRationEditCompProps) => {
	const dispatch = useAppDispatch()
	const rationComponents = useAppSelector(
		state => state.rationSystemReducer.rationComponents,
	)
	const initialDryMatter = toDryMatterInput(dry_matter_percent)
	const [componentTitle, setComponentTitle] = useState(title)
	const [dryMatter, setDryMatter] = useState(initialDryMatter)
	const [titleError, setTitleError] = useState('')
	const [isSaving, setIsSaving] = useState(false)
	const hasChanges =
		componentTitle.trim() !== title.trim() || dryMatter !== initialDryMatter

	const canSave =
		hasChanges &&
		componentTitle.trim() !== '' &&
		isCompleteDryMatter(dryMatter) &&
		!isSaving

	const notifySaveError = () => {
		dispatch(
			notifyUser({
				title: 'Произошла ошибка',
				description: 'Попробуйте снова.',
				isError: true,
			}),
		)
	}

	const handleDryMatterChange = (rawValue: string) => {
		const inputValue = sanitizeDryMatterInput(rawValue)
		if (inputValue !== null) {
			setDryMatter(inputValue)
		}
	}

	const onSubmit = (event: FormEvent) => {
		event.preventDefault()
		if (!canSave) return

		const normalizedTitle = componentTitle.trim()
		const isDuplicate = rationComponents?.some(
			component =>
				component.id !== id && component.title.trim() === normalizedTitle,
		)

		if (isDuplicate) {
			setTitleError('Данное название уже используется')
			notifySaveError()
			return
		}

		const dryMatterPercent = Number(dryMatter)
		setIsSaving(true)
		axAPI({
			method: 'PATCH',
			url: `/ration/components/${id}`,
			data: {
				title: normalizedTitle,
				dry_matter_percent: dryMatterPercent,
			},
		})
			.then(response => {
				if (response.status !== 200 && response.status !== 201) {
					notifySaveError()
					return
				}

				dispatch(
					changeComponentInfo({
						id,
						title: normalizedTitle,
						dry_matter_percent: dryMatterPercent,
					}),
				)
				dispatch(fetchRationComponents())
				dispatch(
					notifyUser({
						title: `СВ,% «${normalizedTitle}» изменен`,
						description: `В рационах, в которых использовался компонент «${normalizedTitle}», произведен перерасчет.`,
						isError: false,
					}),
				)
				setOpenEditComp(false)
			})
			.catch(error => {
				if (getDuplicateIndexes(error).includes(0)) {
					setTitleError('Данное название уже используется')
				}
				notifySaveError()
			})
			.finally(() => {
				setIsSaving(false)
			})
	}

	return (
		<Drawer
			open={openEditComp}
			onClose={() => setOpenEditComp(false)}
			title='Редактирование компонента'
			anchor='right'
			width={490}
		>
			<Stack
				component='form'
				onSubmit={onSubmit}
				sx={{
					flex: 1,
					minHeight: '100%',
					px: '24px',
					boxSizing: 'border-box',
				}}
			>
				<Stack direction='row' gap='8px' alignItems='flex-start'>
					<BarchartInfoLogo
						width={20}
						height={20}
						style={{ color: COLORS.secondaryFont, flexShrink: 0 }}
					/>
					<TypographySecondary14>
						При внесении изменений в СВ% - в рационе, в котором используется
						компонент, автоматически будет произведен перерасчет веса
						компонента, кг/голову.
					</TypographySecondary14>
				</Stack>
				<Stack gap='20px' mt='24px'>
					<Stack gap='24px'>
						<TypographyPrimary sx={{ fontWeight: 600 }}>
							Компонент
						</TypographyPrimary>
						<FloatingField
							value={componentTitle}
							placeholder='Название компонента'
							error={Boolean(titleError)}
							helperText={titleError}
							onChange={nextTitle => {
								setComponentTitle(nextTitle)
								setTitleError('')
							}}
						/>
					</Stack>
					<Stack gap='24px' alignItems='flex-start'>
						<TypographyPrimary sx={{ fontWeight: 600 }}>
							СВ, %
						</TypographyPrimary>
						<FloatingField
							value={dryMatter}
							placeholder='СВ, %'
							width='160px'
							suffix='%'
							centerWhenEmpty
							onChange={handleDryMatterChange}
						/>
					</Stack>
				</Stack>
				<Stack sx={{ mt: 'auto', pb: '24px', pt: '24px' }}>
					<ButtonUI
						type='submit'
						disabled={!canSave}
						sx={{ height: '56px', width: '100%' }}
					>
						Сохранить
					</ButtonUI>
				</Stack>
			</Stack>
		</Drawer>
	)
}

export default PopupRationEditComp
